import { useState, useRef } from 'react';
import {
    Dialog,
    DialogContent,
    DialogActions,
    Typography,
    MenuItem,
    Button,
    Box,
    IconButton,
    styled,
    Backdrop,
    Menu,
} from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import CloseIcon from '@mui/icons-material/Close';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import StatusPill from '../../../Shared/Components/StatusPill';
import { COLORS } from '../../../utils/Application_Constants';
import { PROCESSED_TIMESHEET_STATUS_LABELS } from '../../constants';
import usePatchProcessedTimesheetStatus from '../../hooks/usePatchProcessedTimesheetStatus';

const BackdropBlur = styled(Backdrop)({
    backdropFilter: 'blur(6px)',
    backgroundColor: 'rgba(0, 0, 0, 0.4)',
});

const Textarea = styled('textarea')({
    width: '100%',
    height: '100px',
    resize: 'none',
    border: 'none',
    borderBottom: `1px solid ${COLORS.LT_MIDNIGHT10}`,
    outline: 'none',
    fontFamily: 'inherit',
    fontSize: '1rem',
    padding: '8px 0',
    overflow: 'auto',
    scrollbarWidth: 'none',
    '&::-webkit-scrollbar': {
        display: 'none',
    },
});

const UpdateStatusButtonModal = ({
    id,
    onClose,
    onSuccess,
    currentStatus,
    newStatus,
    setNewStatus,
    statusOptions = [],
}) => {
    const statusButton = useRef(null);
    const queryClient = useQueryClient();
    const [openStatusMenu, setOpenStatusMenu] = useState(false);
    const [isError, setIsError] = useState(false);
    const [statusChangeReason, setStatusChangeReason] = useState('');

    const { mutate: patchProcessedTimesheetStatus, isLoading } = usePatchProcessedTimesheetStatus({
        onSuccess: (updatedTimesheetData) => {
            // Invalidate processed timesheets lists, processed timesheet details, and activity feed
            queryClient.invalidateQueries({ queryKey: ['processedTimesheets'] });
            queryClient.invalidateQueries({
                queryKey: ['processedTimesheetDetails', { processedTimesheetId: id.toString() }],
            });
            queryClient.invalidateQueries({
                queryKey: ['getActivityFeed', { processedTimesheetId: id.toString() }],
            });
            onSuccess(updatedTimesheetData?.needsReviewStatus || updatedTimesheetData?.status);
        },
        onError: () => {
            setIsError(true);
        },
    });

    const handleOpenMenu = () => {
        setOpenStatusMenu(true);
    };
    const handleCloseMenu = () => {
        setOpenStatusMenu(false);
    };
    const handleSelectNewStatus = (state) => {
        setNewStatus(state);
        handleCloseMenu();
    };

    const handleSubmit = () => {
        setIsError(false);
        patchProcessedTimesheetStatus({ processedTimesheetId: id, newStatus, statusChangeReason });
    };

    return (
        <Dialog
            open
            onClose={!isLoading ? onClose : undefined}
            fullWidth
            maxWidth="xs"
            slots={{ backdrop: BackdropBlur }}
            slotProps={{ paper: { sx: { borderRadius: '8px', padding: '48px 36px 24px' } } }}
        >
            <IconButton
                disabled={isLoading}
                onClick={onClose}
                sx={{ position: 'absolute', top: 12, right: 12 }}
            >
                <CloseIcon />
            </IconButton>

            <DialogContent sx={{ padding: '0px' }}>
                <Box display="flex" justifyContent="flex-start" gap="50px" mb={2}>
                    <Box display="flex" flexDirection="column" alignItems="flex-start" gap="8px">
                        <Typography
                            sx={{
                                fontWeight: 600,
                                fontSize: '12px',
                                lineHeight: '100%',
                                letterSpacing: '0.07rem',
                                textTransform: 'uppercase',
                                color: COLORS.LT_MIDNIGHT500,
                            }}
                        >
                            FROM
                        </Typography>
                        <StatusPill status={currentStatus} sx={{ padding: 2 }} />
                    </Box>
                    <Box display="flex" flexDirection="column" alignItems="flex-start" gap="8px">
                        <Typography
                            sx={{
                                fontWeight: 600,
                                fontSize: '12px',
                                lineHeight: '100%',
                                letterSpacing: '0.07rem',
                                textTransform: 'uppercase',
                                color: COLORS.LT_MIDNIGHT500,
                            }}
                        >
                            TO
                        </Typography>
                        <StatusPill
                            status={newStatus}
                            content={
                                <Box display="flex" alignItems="center" gap={0.5}>
                                    {PROCESSED_TIMESHEET_STATUS_LABELS[newStatus] ||
                                        'Select status'}
                                    <KeyboardArrowDownIcon
                                        sx={{
                                            ml: 'auto',
                                            transition: 'transform 0.3s',
                                            transform: openStatusMenu
                                                ? 'rotate(180deg)'
                                                : 'rotate(0deg)',
                                        }}
                                    />
                                </Box>
                            }
                            sx={{ padding: 2 }}
                            ref={statusButton}
                            onClick={handleOpenMenu}
                        />

                        <Menu
                            id="change-status-options"
                            open={openStatusMenu}
                            anchorEl={statusButton.current}
                            onClose={handleCloseMenu}
                        >
                            {statusOptions.map((state) => (
                                <MenuItem
                                    key={state}
                                    onClick={() => handleSelectNewStatus(state)}
                                    disableRipple
                                >
                                    <StatusPill status={state} sx={{ minWidth: 146 }} />
                                </MenuItem>
                            ))}
                        </Menu>
                    </Box>
                </Box>

                <Box display="flex" flexDirection="column">
                    <Typography
                        sx={{ color: COLORS.BLACK, fontSize: '16px', fontWeight: '600' }}
                        mb={2}
                    >
                        Reason for changing status
                    </Typography>

                    <Box mb={1}>
                        <Textarea
                            placeholder="Add comment..."
                            value={statusChangeReason}
                            onChange={(e) => setStatusChangeReason(e.target.value)}
                            maxLength={800}
                        />
                        <Box display="flex" justifyContent="flex-start">
                            <Typography variant="caption" color={COLORS.LT_MI}>
                                {statusChangeReason.length}/800
                            </Typography>
                        </Box>
                    </Box>

                    {isError && (
                        <Typography color="error" my={2}>
                            There was an error changing status. Please try again.
                        </Typography>
                    )}

                    <DialogActions sx={{ justifyContent: 'flex-end', padding: 0 }}>
                        <Button
                            disabled={isLoading}
                            color="error"
                            onClick={onClose}
                            sx={{ p: '8px 10px', height: '33px' }}
                        >
                            Cancel
                        </Button>
                        <Button
                            loading={isLoading}
                            variant="contained"
                            color="primary"
                            sx={{
                                borderRadius: ({ shape }) => `${shape.borderRadiusVariants.md}px`,
                                p: '8px 10px',
                                width: '120px',
                                height: '33px',
                            }}
                            onClick={handleSubmit}
                        >
                            Change status
                        </Button>
                    </DialogActions>
                </Box>
            </DialogContent>
        </Dialog>
    );
};

export default UpdateStatusButtonModal;
